/**
 * Viewer-scoped `viewerShareBalance` realtime patcher.
 *
 * `useGetProposal.viewerShareBalance` is the per-viewer scalar (D6
 * string) the backend populates only when the request carries a valid
 * `morfi_session` cookie — derived from the latest
 * `market_backs.backer_balance_d6` event. Replaces the historical
 * `funding[]` projection which summed lifetime gross BACK rows from the
 * append-only `market_backs` hypertable and silently disagreed with
 * the on-chain ATA after a back+unback cycle.
 *
 * This helper applies a signed D6 delta to that scalar live: positive
 * on BACK (chain mints share-mint tokens), negative on UNBACK (chain
 * burns them). The delta is the indexer-published
 * `MarketBacking.sharesDeltaD6` — wired through the typed proto and the existing
 * `backing_activity_with_actor` builder, so no proto / publisher
 * change was needed.
 *
 * Three filters guard correctness:
 *
 *   1. The viewer-filter is STRICT: when `args.viewerWallet` is
 *      undefined / empty (anonymous, SSR pre-auth, demo mode) the
 *      helper bails and the row's existing `viewerShareBalance` stays
 *      untouched.
 *   2. Cross-actor events are ignored — only events whose
 *      `payload.actor === args.viewerWallet` mutate the row. Other
 *      users' backs/unbacks must not change the viewer's chip.
 *   3. Per-signature dedup via a bounded module-level LRU — JetStream
 *      replay on tab-switch / reconnect can deliver the same event
 *      twice (filter-manager uses `deliver_policy: by_start_sequence`).
 *      The previous helper had no dedup → "Your stake" inflated
 *      linearly with replay count. Backing event signatures are unique
 *      on-chain tx sigs, so cross-row dedup is safe: a given signature
 *      is the SAME chain event regardless of which cache row observes
 *      it. The LRU is bounded so a long-lived tab cannot leak unbounded
 *      memory.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

import { addD6String } from './d6.js';

const MAX_TRACKED_SIGS = 1024;

/**
 * Bounded LRU of applied backing-event signatures. Module-level
 * intentionally — the dedup is keyed off the on-chain tx signature
 * which uniquely identifies a chain event across the whole app, so
 * sharing the Set across queries is correct (no false-positive cross-
 * query suppressions are possible because signatures are unique chain
 * primary keys).
 *
 * Insertion order is the LRU order: a fresh `add` re-inserts at the
 * end so frequently-touched sigs stay hot. When the Set hits
 * `MAX_TRACKED_SIGS`, we drop the oldest entries until back at the
 * cap — bounded memory in a long-lived tab.
 */
const appliedSignatures = new Set<string>();

function rememberSignature(sig: string): void {
  // Re-insert to bump LRU position.
  appliedSignatures.delete(sig);
  appliedSignatures.add(sig);
  if (appliedSignatures.size > MAX_TRACKED_SIGS) {
    const overflow = appliedSignatures.size - MAX_TRACKED_SIGS;
    let dropped = 0;
    for (const s of appliedSignatures) {
      if (dropped >= overflow) break;
      appliedSignatures.delete(s);
      dropped += 1;
    }
  }
}

/**
 * Test-only: clear the dedup LRU. Exported for unit-test isolation.
 */
export function _resetViewerShareBalanceDedup(): void {
  appliedSignatures.clear();
}

function coerceSignedD6(amount: unknown): string | null {
  if (typeof amount === 'bigint') return amount.toString();
  if (typeof amount === 'number' && Number.isFinite(amount)) {
    return Math.trunc(amount).toString();
  }
  if (typeof amount === 'string') {
    const trimmed = amount.trim();
    if (trimmed === '' || !/^-?\d+$/.test(trimmed)) return null;
    return trimmed;
  }
  return null;
}

function readSignature(raw: Record<string, unknown>): string | null {
  const sig = raw.signature;
  if (typeof sig === 'string' && sig.length > 0) return sig;
  return null;
}

/**
 * Apply one decoded `MarketBacking` event to the viewer's
 * `viewerShareBalance` scalar — only when the event's `actor` matches
 * `args.viewerWallet`, and only the first time the event's `signature`
 * is seen by this module.
 *
 * Returns `prev` verbatim when:
 *   - `args.viewerWallet` is not set (anonymous viewer)
 *   - `event.actor !== args.viewerWallet` (someone else's back/unback)
 *   - the row doesn't carry `viewerShareBalance` (field not projected)
 *   - `payload.sharesDeltaD6` is missing or unparseable
 *   - `payload.signature` was already applied
 *
 * Otherwise emits a NEW row object with `viewerShareBalance` patched
 * by `addD6String(prev, sharesDeltaD6)` and the signature recorded in
 * the module-level dedup LRU.
 */
export function applyViewerShareBalanceDelta<
  T extends {
    readonly viewerShareBalance?: string;
  },
>(prev: T, raw: Record<string, unknown>, args: Record<string, unknown>): T {
  // Anonymous viewer — leave the row alone.
  const viewerWallet = typeof args.viewerWallet === 'string' ? (args.viewerWallet as string) : null;
  if (viewerWallet === null || viewerWallet.length === 0) return prev;

  // Not the viewer's event.
  const actor = typeof raw.actor === 'string' ? (raw.actor as string) : null;
  if (actor === null || actor !== viewerWallet) return prev;

  // The row didn't project `viewerShareBalance` — nothing to patch.
  if (prev.viewerShareBalance === undefined) return prev;

  const delta = coerceSignedD6(raw.sharesDeltaD6);
  if (delta === null) return prev;

  // Dedup-by-signature: JetStream `by_start_sequence` replay on
  // reconnect/re-bind can re-deliver the same event N times. Apply at
  // most once per signature (module-level LRU — see top-of-file comment
  // on why cross-row dedup is safe).
  const signature = readSignature(raw);
  if (signature !== null && appliedSignatures.has(signature)) {
    return prev;
  }

  const next: T = { ...prev, viewerShareBalance: addD6String(prev.viewerShareBalance, delta) };
  if (signature !== null) rememberSignature(signature);
  return next;
}
